import { Schema } from "effect";

/**
 * System role keys seeded in Phase 1. Additional roles are data rows later.
 * `super_admin` is a boolean on `users`, not a grantable role key.
 */
export const SYSTEM_ROLE_KEYS = ["member", "operators_admin"] as const;
export type SystemRoleKey = (typeof SYSTEM_ROLE_KEYS)[number];
export const SystemRoleKey = Schema.Literals(SYSTEM_ROLE_KEYS);

/**
 * Atomic permission vocabulary shared by control-plane and web.
 * `tool.<slug>.access` entries are added as tools ship; Operators is first.
 */
export const PERMISSION_KEYS = [
  "session.read",
  "session.drive",
  "session.stop",
  "session.archive",
  "usage.read_self",
  "usage.read_org",
  "admin.inspect_vms",
  "admin.roles.manage",
  "tool.operators.access",
  "tool.dashboards.access",
  "tool.dashboards.artifacts.publish",
  "tool.okrs.access",
  "tool.okrs.write",
  "tool.okrs.manage",
  "skills.manage_workspace",
  "tool.event_queue.access",
  "tool.event_queue.manage",
] as const;
export type PermissionKey = (typeof PERMISSION_KEYS)[number];
export const PermissionKey = Schema.Literals(PERMISSION_KEYS);

/** Baseline grants for every signed-in employee (includes today's org-wide usage). */
export const MEMBER_PERMISSION_KEYS = [
  "session.read",
  "session.drive",
  "usage.read_self",
  "usage.read_org",
  "tool.dashboards.access",
  "tool.okrs.access",
  "tool.okrs.write",
  "tool.okrs.manage",
] as const satisfies readonly PermissionKey[];

/** Former JWT/DB `admin` — session manage + VM inspect. */
export const OPERATORS_ADMIN_PERMISSION_KEYS = [
  ...MEMBER_PERMISSION_KEYS,
  "session.stop",
  "session.archive",
  "admin.inspect_vms",
  "skills.manage_workspace",
  "tool.event_queue.access",
  "tool.event_queue.manage",
  "tool.dashboards.artifacts.publish",
] as const satisfies readonly PermissionKey[];

export const SYSTEM_ROLE_PERMISSION_SEEDS = {
  member: MEMBER_PERMISSION_KEYS,
  operators_admin: OPERATORS_ADMIN_PERMISSION_KEYS,
} as const satisfies Record<SystemRoleKey, readonly PermissionKey[]>;

export const SYSTEM_ROLE_DEFINITIONS = [
  {
    key: "member",
    name: "Member",
    description: "Baseline access for every JIT-provisioned employee",
  },
  {
    key: "operators_admin",
    name: "Operators admin",
    description: "Stop/patch any session and inspect Daytona VMs",
  },
] as const satisfies ReadonlyArray<{
  key: SystemRoleKey;
  name: string;
  description: string;
}>;

export const PERMISSION_DEFINITIONS = [
  { key: "session.read", name: "Read sessions", toolSlug: "operators" },
  { key: "session.drive", name: "Drive sessions", toolSlug: "operators" },
  { key: "session.stop", name: "Stop any session", toolSlug: "operators" },
  { key: "session.archive", name: "Archive sessions", toolSlug: "operators" },
  { key: "usage.read_self", name: "Read own usage", toolSlug: null },
  { key: "usage.read_org", name: "Read org usage breakdown", toolSlug: null },
  { key: "admin.inspect_vms", name: "Inspect Daytona VMs", toolSlug: null },
  { key: "admin.roles.manage", name: "Manage roles and grants", toolSlug: null },
  { key: "tool.operators.access", name: "Access Operators tool", toolSlug: "operators" },
  { key: "tool.dashboards.access", name: "Access Dashboards tool", toolSlug: "dashboards" },
  {
    key: "tool.dashboards.artifacts.publish",
    name: "Publish, unpublish, and roll back dashboard artifacts",
    toolSlug: "dashboards",
  },
  { key: "tool.okrs.access", name: "Access OKRs tool", toolSlug: "okrs" },
  { key: "tool.okrs.write", name: "Create and update OKRs", toolSlug: "okrs" },
  { key: "tool.okrs.manage", name: "Manage OKR cycles", toolSlug: "okrs" },
  { key: "skills.manage_workspace", name: "Manage all skills", toolSlug: "operators" },
  { key: "tool.event_queue.access", name: "Access Event queue tool", toolSlug: "event-queue" },
  {
    key: "tool.event_queue.manage",
    name: "Retry, discard, and pause event queues",
    toolSlug: "event-queue",
  },
] as const satisfies ReadonlyArray<{
  key: PermissionKey;
  name: string;
  toolSlug: string | null;
}>;
